import { useState, useCallback, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Package, Settings, GripVertical, Power, PowerOff, Trash2, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { ModuleConfigurationAdmin } from '@/types';

interface SortableModuleCardProps {
  module: ModuleConfigurationAdmin;
  onEdit: (module: ModuleConfigurationAdmin) => void;
  onToggleEnabled: (key: string, enabled: boolean) => void;
  onDelete: (moduleKey: string) => void;
  isUpdating?: boolean | undefined;
}

function SortableModuleCard({
  module,
  onEdit,
  onToggleEnabled,
  onDelete,
  isUpdating,
}: SortableModuleCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.moduleKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        className={cn(
          'transition-opacity',
          !module.isEnabled && 'opacity-60',
          isDragging && 'opacity-50 shadow-lg',
        )}
      >
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <button
                type="button"
                className="mt-1 cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing touch-none"
                aria-label={`Reorder ${module.displayName}`}
                {...attributes}
                {...listeners}
              >
                <GripVertical className="h-5 w-5" />
              </button>
              <div>
                <CardTitle className="flex items-center gap-2 flex-wrap">
                  {module.displayName}
                  <Badge variant={module.isEnabled ? 'default' : 'secondary'}>
                    {module.isEnabled ? (
                      <>
                        <Power className="h-3 w-3 mr-1" />
                        Enabled
                      </>
                    ) : (
                      <>
                        <PowerOff className="h-3 w-3 mr-1" />
                        Disabled
                      </>
                    )}
                  </Badge>
                  {module.userMigrationGetEndpoint && (
                    <Badge variant="outline" className="text-xs font-normal">
                      <Users className="h-3 w-3 mr-1" />
                      User Migration
                      {!module.userMigrationPutEndpoint && ' (no write-back)'}
                    </Badge>
                  )}
                </CardTitle>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={module.isEnabled}
                onCheckedChange={(enabled) => onToggleEnabled(module.moduleKey, enabled)}
                disabled={isUpdating}
                aria-label={`Toggle ${module.displayName}`}
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onEdit(module)}
                aria-label={`Edit ${module.displayName}`}
              >
                <Settings className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => onDelete(module.moduleKey)}
                aria-label={`Delete ${module.displayName}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Module Key</span>
              <p className="font-medium font-mono text-xs">{module.moduleKey}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Base URL</span>
              <p className="font-medium font-mono text-xs truncate" title={module.baseUrl ?? undefined}>
                {module.baseUrl ?? <span className="italic text-muted-foreground font-sans">System default</span>}
              </p>
            </div>
            <div>
              <span className="text-muted-foreground">ID Format</span>
              <p className="font-medium font-mono text-xs">{module.sourceIdPlaceholder ?? '—'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Timeout</span>
              <p className="font-medium">{module.timeoutSeconds}s</p>
            </div>
          </div>

          {/* Endpoint URLs */}
          <div className="mt-4 pt-4 border-t space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Validate</span>
              <code className="text-xs bg-muted px-2 py-1 rounded">
                {module.validateEndpoint ?? '—'}
              </code>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Migration</span>
              <code className="text-xs bg-muted px-2 py-1 rounded">
                {module.migrateEndpoint ?? '—'}
              </code>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface ModuleConfigListProps {
  modules: ModuleConfigurationAdmin[];
  onEdit: (module: ModuleConfigurationAdmin) => void;
  onToggleEnabled: (key: string, enabled: boolean) => void;
  onDelete: (moduleKey: string) => void;
  onReorder: (orderedKeys: string[]) => void;
  isUpdating?: boolean | undefined;
  className?: string | undefined;
}

export function ModuleConfigList({
  modules,
  onEdit,
  onToggleEnabled,
  onDelete,
  onReorder,
  isUpdating,
  className,
}: ModuleConfigListProps) {

  // Optimistic local order so cards don't snap back during async save
  const [optimisticOrder, setOptimisticOrder] = useState<string[] | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Sort modules by displayOrder from server
  const serverSorted = [...modules].sort((a, b) => a.displayOrder - b.displayOrder);

  // Apply optimistic order if active, otherwise use server order
  const sortedModules = optimisticOrder
    ? optimisticOrder
        .map((key) => modules.find((m) => m.moduleKey === key))
        .filter((m): m is ModuleConfigurationAdmin => m != null)
    : serverSorted;
  const sortedKeys = sortedModules.map((m) => m.moduleKey);

  // Reset optimistic order when server data catches up
  useEffect(() => {
    if (optimisticOrder) {
      const serverKeys = serverSorted.map((m) => m.moduleKey);
      if (JSON.stringify(serverKeys) === JSON.stringify(optimisticOrder)) {
        setOptimisticOrder(null);
      }
    }
  }, [modules, optimisticOrder, serverSorted]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedKeys.indexOf(active.id as string);
      const newIndex = sortedKeys.indexOf(over.id as string);
      const newOrder = arrayMove(sortedKeys, oldIndex, newIndex);
      setOptimisticOrder(newOrder);
      onReorder(newOrder);
    }
  }, [sortedKeys, onReorder]);

  return (
    <div className={cn('space-y-4', className)}>
      {sortedModules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No modules configured.</p>
          </CardContent>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortedKeys} strategy={verticalListSortingStrategy}>
            {sortedModules.map((module) => (
              <SortableModuleCard
                key={module.moduleKey}
                module={module}
                onEdit={onEdit}
                onToggleEnabled={onToggleEnabled}
                onDelete={onDelete}
                isUpdating={isUpdating}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
